import { useEffect, useState } from 'react'
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ContainerGroupField } from '@/components/common'
import {
    AutocompleteControl,
    InputControl,
    PhoneNumberControl,
    RadioGroupControl
} from '@/components/form-controls'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { OptionsField } from '@/types/forms/optionsField'
import { BasicInfoFormData } from '@/types/forms/basicInfoForm'
import { inputValidations } from '@/utils'
import { isColCellphone } from '@/utils/validators'

interface PersonalDataControlProps {
    control: Control<BasicInfoFormData>
    optionsBoolean: OptionsField[]
    text: string
    watch: UseFormWatch<BasicInfoFormData>
    setValue: UseFormSetValue<BasicInfoFormData>
}

// eslint-disable-next-line max-lines-per-function
export const PersonalDataControl = ({
    control,
    optionsBoolean,
    text,
    watch,
    setValue
}: PersonalDataControlProps): JSX.Element => {
    const { t } = useTranslation()
    const resourcesApi = useResourcesApi()
    const [hasFetched, setHasFetched] = useState<boolean>(false)
    const [optionsGenders, setOptionsGenders] = useState<OptionsField[]>([])
    const [optionsMaritalStatus, setOptionsMaritalStatus] = useState<
        OptionsField[]
    >([])
    const [isRetrying, setIsRetrying] = useState<boolean>(false)
    const [hasError, setHasError] = useState<boolean>(false)
    const [retryCount, setRetryCount] = useState<number>(0)

    useEffect(() => {
        let mounted = true

        if (hasFetched) {
            return
        }

        const fetchAllOptions = async (): Promise<void> => {
            const maxRetries = 3

            // eslint-disable-next-line complexity
            const fetchWithRetry = async (attempt = 0): Promise<void> => {
                try {
                    if (attempt > 0) {
                        setIsRetrying(true)
                        setRetryCount(attempt)
                    }

                    const [responseMaritalStatus, responseGenders] =
                        await Promise.all([
                            resourcesApi.getMaritalStatus(),
                            resourcesApi.getGenders()
                        ])

                    if (!mounted) {
                        return
                    }

                    // Verificar que ambas respuestas tengan datos válidos
                    const hasValidMaritalStatus =
                        Array.isArray(responseMaritalStatus) &&
                        responseMaritalStatus.length > 0
                    const hasValidGenders =
                        Array.isArray(responseGenders) &&
                        responseGenders.length > 0

                    if (hasValidMaritalStatus && hasValidGenders) {
                        setOptionsMaritalStatus(
                            responseMaritalStatus
                                .filter((item) => item?.value && item?.label)
                                .map((item) => ({
                                    value: item.value,
                                    label: item.label
                                }))
                        )

                        setOptionsGenders(
                            responseGenders
                                .filter((item) => item?.id && item?.name)
                                .map((item) => ({
                                    value: item.id,
                                    label: item.name
                                }))
                        )
                        setHasFetched(true)
                        setIsRetrying(false)
                        setHasError(false)

                        return
                    }

                    // Si no hay datos válidos y podemos reintentar
                    if (mounted && attempt < maxRetries) {
                        setTimeout(
                            () => {
                                if (mounted) {
                                    fetchWithRetry(attempt + 1)
                                }
                            },
                            1000 * (attempt + 1)
                        )
                    } else if (mounted) {
                        // Si no podemos reintentar, mostrar error
                        setOptionsMaritalStatus([])
                        setOptionsGenders([])
                        setHasFetched(true)
                        setIsRetrying(false)
                        setHasError(true)
                    }
                } catch (error) {
                    console.error(
                        `Failed to fetch resources (attempt ${attempt + 1}):`,
                        error
                    )

                    if (mounted && attempt < maxRetries) {
                        setTimeout(
                            () => {
                                if (mounted) {
                                    fetchWithRetry(attempt + 1)
                                }
                            },
                            1000 * (attempt + 1)
                        )
                    } else if (mounted) {
                        setOptionsMaritalStatus([])
                        setOptionsGenders([])
                        setHasFetched(true)
                        setIsRetrying(false)
                        setHasError(true)
                    }
                }
            }

            fetchWithRetry()
        }

        fetchAllOptions()

        return (): void => {
            mounted = false
        }
    }, [resourcesApi, hasFetched])

    return (
        <ContainerGroupField
            tittleGroup={t('forms.groups.personalInformation')}
        >
            {/* Mensaje de reintento */}
            {isRetrying && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-blue-700 text-sm">
                            Reintentando carga de información... (Intento{' '}
                            {retryCount}/3)
                        </span>
                    </div>
                </div>
            )}

            {/* Mensaje de error */}
            {hasError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                        <div className="text-red-500 mr-2">⚠️</div>
                        <div className="text-red-700 text-sm">
                            <p className="font-medium">
                                Error al cargar información
                            </p>
                            <p className="text-xs mt-1">
                                Por favor, recargue la página para intentar
                                nuevamente.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <AutocompleteControl
                name="idGender"
                control={control}
                label={t(`${text}.gender`)}
                options={optionsGenders}
                rules={{
                    required: t(`${text}.required.gender`)
                }}
            />
            <AutocompleteControl
                name="idCivilStatus"
                control={control}
                label={t(`${text}.civilStatus`)}
                options={optionsMaritalStatus}
                rules={{
                    required: t(`${text}.required.civilStatus`)
                }}
            />
            <PhoneNumberControl
                name="cellphone"
                control={control}
                label={t('forms.common.cellphoneNumber')}
                rules={{
                    required: t('forms.common.cellphoneNumber'),
                    validate: (value: string) =>
                        isColCellphone(value) ||
                        t('forms.common.invalidPhoneNumber')
                }}
            />
            <RadioGroupControl
                name="haveChildren"
                control={control}
                label={t(`${text}.haveChildren`)}
                options={optionsBoolean}
                rules={{
                    required: t(`${text}.required.haveChildren`)
                }}
            />
        </ContainerGroupField>
    )
}
